import { FormSectionHandler } from '../entity.handler';
import { ThemeCustomHTMLTag, ThemeResponse, ThemeUpdateRequest } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ComboField, MemoField, postImageForm, dataURItoFile } from '../utils';
import { ICONS } from '../icons';
import { getThemeDictionary } from 'app/shared/components/theme/theme-dictionary';
import { ThemeJsonModalComponent, ThemeJsonModalOptions } from './json-modal/json-modal.component';
import { ThemeEngine } from 'app/shared/components/theme';
import { ThemeCssModalComponent, ThemeCssModalOptions } from './css-modal/css-modal.component';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';
import  Bluebird from 'bluebird';
import { HttpClient } from '@angular/common/http';

interface EditorValues {
    theme?: string;
    name?: string;
    description?: string;
    cssSrc?: string;
    customTags?: ListFieldValue<ThemeCustomHTMLTag>[];
    logoWeb?: { cropped: string, preview: string };
}

interface CustomTag extends ThemeCustomHTMLTag {
    id: string;
}

export class ThemeDetailsSectionHandler implements FormSectionHandler<ThemeResponse, EditorValues> {

    type = 'form';
    icon = ICONS.theme;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    themeDictionary = getThemeDictionary();

    actions = [
        this.admin.user.owner.type === 'system' ? { id: 'json', name: this.translate('JSON'), showAlways: true } : null,
        this.admin.user.owner.type === 'system' ? { id: 'css', name: this.translate('CSS'), showAlways: true } : null,
    ].filter(x => x);

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean,
        private legacy: boolean,
        public themeEngine: ThemeEngine,
        public httpClient: HttpClient
    ) {
    }

    private properties = ['theme', 'name', 'description'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.THEME.DETAILS.${key}`, values);
    }

    async applyUpdate(entity: ThemeResponse, payload: ThemeUpdateRequest, reload = false): Promise<ThemeResponse> {

        const updateLogo = async (name: string, size: string) => {
            if (payload[name]) {
                const logo = payload[name];
                delete payload[name];
                if (logo.cropped && logo.cropped.startsWith('data:')) {
                    await postImageForm(this.httpClient, `${this.admin.apiEndpoint}/entities/themes/${entity.id}/logo?size=${size}`, this.admin.apiAccessToken, dataURItoFile(logo.cropped));
                    await Bluebird.delay(500); // wait a while as the api rate limiting is gonna freak out if we do 3 updates in quick succession
                }
                if (logo.cropped === null) {
                    await this.admin.applyUpdate(api => api.entities.deleteThemeLogo(entity.id, size));
                    await Bluebird.delay(500); // wait a while as the api rate limiting is gonna freak out if we do 3 updates in quick succession
                }
            }
        };
        await updateLogo('logoWeb', 'small');
        return this.admin.applyUpdate(async api => {
            const theme = await api.entities.updateTheme(entity.id, payload);
            if (reload) {
                await Bluebird.delay(100); // the editor re-reads the theme on reload, give replication a chance to catch up
            }
            return theme;
        }, {
            _type: 'theme',
            _reload: reload,
            _cacheKey: `getTheme/${entity.id}`,
        });
    }

    async mapEntityToEditor(entity: ThemeResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity, {
            cssSrc: entity.settings?.cssSrc || '',
        });
        result.customTags = (entity.customTags && entity.customTags.map(x => this.stateItemToListItem({ ...x, id: uuid.v4() }))) || [];
        const logoWeb = `${this.admin.apiEndpoint}/entities/themes/${entity.id}/logo?size=small&_=${(new Date()).getTime()}`;
        result.logoWeb = {
            preview: logoWeb,
            cropped: logoWeb,
        };
        return result;
    }

    async mapEditorToEntity(entity: ThemeUpdateRequest, values: EditorValues): Promise<ThemeUpdateRequest> {
        const result = EntityUtils.mergeFields<ThemeUpdateRequest>(this.properties, values, entity);
        result.settings = {
            ...result.settings || {},
            cssSrc: values.cssSrc,
        };
        result.customTags = values.customTags ? values.customTags.map(x => x.data) : undefined;

        if (values.logoWeb) {
            result['logoWeb'] = {
                preview: values.logoWeb.preview,
                cropped: values.logoWeb.preview,
            };
        }
        return result;
    }

    getFields(entity: ThemeResponse): Promise<FormBuilderField[]> {

        return Promise.resolve().then(() => {
            const baseThemes = Object.entries(this.themeDictionary).map(key => ({ key: key[0], value: key[1].name }));
            return [
                !this.creating && this.legacy ? { ...ComboField('theme', this.translate('FIELDS.BASE_THEME'), true, baseThemes), readonly: !this.creating } : null,
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                TextField('description', this.translate('FIELDS.DESCRIPTION'), false, 1, 255),
                TextField('cssSrc', this.translate('FIELDS.CSS_SRC'), false, 1, 255),
                !this.creating && {
                    id: 'customTags', title: this.translate('FIELDS.CUSTOM_TAGS'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFilterModal.bind(this), },
                        { id: 'edit', click: this.showFilterModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
                !this.creating && {
                    id: 'logoWeb', title: this.translate('FIELDS.WEB_LOGO'), type: 'image', description: this.translate('FIELDS.WEB_LOGO_DESC'), options: { background: 'transparent', preview: { width: 480, height: 240 } }, width: 120
                },
            ].filter(x => x);
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));
    }

    async actionTriggered(entity: ThemeResponse, action: string): Promise<void> {
        if (action === 'json') {

            this.admin.modal.open<ThemeJsonModalOptions>(ThemeJsonModalComponent, {
                data: {
                    settings: entity.settings,
                },
                actions: {
                    close: () => {
                        this.admin.modal.close();
                    },
                    save: (settings: any) => {
                        return this.applyUpdate(entity, { settings: settings }, true).then(theme => {
                            this.themeEngine.settingsToVariables(theme.settings);
                            return theme;
                        });
                    },
                },
            });


        }
        if (action === 'css') {

            this.admin.modal.open<ThemeCssModalOptions>(ThemeCssModalComponent, {
                data: {
                    css: entity.customStyles || '',
                },
                actions: {
                    close: () => {
                        this.admin.modal.close();
                    },
                    save: (css: any) => {
                        return this.applyUpdate(entity, { customStyles: css }, true).then(theme => {
                            this.themeEngine.settingsToVariables(theme.settings);
                            return theme;
                        });
                    },
                },
            });


        }
    }

    stateItemToListItem(item: CustomTag): ListFieldValue<ThemeCustomHTMLTag> {
        return {
            id: item.id,
            name: `${item.name}`,
            icon: 'code',
            description: undefined,
            data: item,
        };
    }

    updateOrAddItem(items: ListFieldValue<ThemeCustomHTMLTag>[], existingItem: ListFieldValue<ThemeCustomHTMLTag>, item: CustomTag) {
        if (existingItem) {
            const idx = items.findIndex(x => x.id === existingItem.id);
            items[idx] = this.stateItemToListItem(item);
        } else {
            items.push(this.stateItemToListItem(item));
        }
    }

    showFilterModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<ThemeCustomHTMLTag> {

        const translate = (key) => this.translate('MODALS.CUSTOM_TAG.' + key);

        return this.admin.showFormModal<CustomTag>({
            groups: [{
                name: translate('TITLE'),
                description: translate('DESC'),
                fields: [
                    TextField('name', translate('FIELDS.NAME'), true, 1, 255),
                    ComboField('location', translate('FIELDS.LOCATION'), true, [
                        { key: 'head', value: translate('FIELDS.LOCATION_HEADER') },
                        { key: 'body', value: translate('FIELDS.LOCATION_BODY') },
                    ]),
                    TextField('tag', translate('FIELDS.TAG_TYPE'), true, 1, 25),
                    { ...MemoField('attributes', translate('FIELDS.TAG_ATTRIBUTES'), false, 1, 4096), options: { size: '5' }, description: translate('FIELDS.TAG_ATTRIBUTES_DESC') },
                    { ...MemoField('contents', translate('FIELDS.TAG_CONTENTS'), false, 1, 4096), options: { size: '10' }, },
                ],
            }],
        }, item?.data || { // pass some defaults for new items
            id: uuid.v4(),
            name: '',
            tag: 'script',
            location: 'head',
            attributes: '',
            contents: '',
        }).then(result => {
            if (result) {
                this.updateOrAddItem(values.customTags, item, result);
            }
            return result;
        });


    }


}
